export type GL = WebGL2RenderingContext;

/** Programs linked so far, per context, by their sources. */
const linked = new WeakMap<GL, Map<string, WebGLProgram>>();

export class Program {
  readonly program: WebGLProgram;
  private uniforms = new Map<string, WebGLUniformLocation | null>();

  constructor(
    readonly gl: GL,
    vertex: string,
    fragment: string,
  ) {
    // Compiled once per context and source (a renderer built again for the
    // same canvas reuses them: compiling took a good part of a rebuild).
    const cache = linked.get(gl) ?? new Map<string, WebGLProgram>();
    linked.set(gl, cache);
    const key = `${vertex} ${fragment}`;
    let p = cache.get(key);
    if (!p) {
      p = gl.createProgram()!;
      gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vertex));
      gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fragment));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`Shader link failed: ${gl.getProgramInfoLog(p)}`);
      cache.set(key, p);
    }
    this.program = p;
  }

  use(): void {
    this.gl.useProgram(this.program);
  }

  u(name: string): WebGLUniformLocation | null {
    let loc = this.uniforms.get(name);
    if (loc === undefined) {
      loc = this.gl.getUniformLocation(this.program, name);
      this.uniforms.set(name, loc);
    }
    return loc;
  }

  attrib(name: string): number {
    return this.gl.getAttribLocation(this.program, name);
  }
}

function compile(gl: GL, type: number, source: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, source);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s);
    const numbered = source
      .split("\n")
      .map((l, i) => `${String(i + 1).padStart(3)} ${l}`)
      .join("\n");
    throw new Error(`Shader compile failed: ${log}\n${numbered}`);
  }
  return s;
}

export interface TextureOptions {
  internal: number;
  format: number;
  type: number;
  /** Filtered with mipmaps (zoomed-out sampling) instead of nearest texels. */
  mipmaps?: boolean;
}

/** A 2D texture covering the hex grid (one texel per hex). */
export class GridTexture {
  readonly texture: WebGLTexture;

  constructor(
    readonly gl: GL,
    readonly width: number,
    readonly height: number,
    readonly opts: TextureOptions,
    data: ArrayBufferView | null,
  ) {
    this.texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, opts.internal, width, height, 0, opts.format, opts.type, data);
    const min = opts.mipmaps ? gl.LINEAR_MIPMAP_LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, min);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, opts.mipmaps ? gl.LINEAR : gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (opts.mipmaps) gl.generateMipmap(gl.TEXTURE_2D);
  }

  upload(data: ArrayBufferView): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.width, this.height, this.opts.format, this.opts.type, data);
    if (this.opts.mipmaps) gl.generateMipmap(gl.TEXTURE_2D);
  }

  /** Uploads only the rectangle [x, x + w) x [y, y + h) of `data` (the
   *  whole grid's, row-major): a big map's armies are a small part of it. */
  uploadRect(data: ArrayBufferView, x: number, y: number, w: number, h: number): void {
    if (w <= 0 || h <= 0) return;
    if (x === 0 && y === 0 && w === this.width && h === this.height) return this.upload(data);
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, this.width);
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, x);
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, y);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, w, h, this.opts.format, this.opts.type, data);
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
    if (this.opts.mipmaps) gl.generateMipmap(gl.TEXTURE_2D);
  }

  /** Frees the texture (the GPU's memory back). */
  delete(): void {
    this.gl.deleteTexture(this.texture);
  }

  bind(unit: number): void {
    this.gl.activeTexture(this.gl.TEXTURE0 + unit);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
  }
}

/** Parses "#rrggbb" into [r, g, b] in 0..1. */
export function rgb(hex: string): [number, number, number] {
  const v = parseInt(hex.replace("#", ""), 16);
  return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
}
